import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuotePage() {
  const { token } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-quote", token],
    queryFn: () => axios.get(`${BACKEND_URL}/api/v1/public-quotes/${token}`).then((r) => r.data.data),
  });

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]">Loading your quotation...</div>;
  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]" data-testid="public-quote-error">
        <p className="text-[#6E6760]">This quotation link is invalid, expired or has been revoked.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] py-12 px-4" data-testid="public-quote-page">
      <div className="max-w-3xl mx-auto">
        <header className="text-center mb-10">
          <p className="font-serif-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#2A211D]">Your Quotation</p>
          <p className="text-sm text-[#6E6760] mt-2">Prepared exclusively for you</p>
        </header>

        <div className="bg-white border border-[#E5DEC9] rounded-2xl shadow-sm p-6 sm:p-8">
          <div className="space-y-4 mb-6">
            {data.items.map((item, idx) => (
              <div key={idx} className="flex items-start justify-between border-b border-[#F3EFE9] pb-4" data-testid={`public-quote-item-${idx}`}>
                <div>
                  <p className="font-heading font-semibold">{item.specSnapshot?.productName || item.shopReference}</p>
                  <p className="text-xs text-[#8C8379] font-mono-spec uppercase mt-1">
                    {item.catalogueCompanyName} · {item.catalogueVersionName}
                  </p>
                  <p className="text-sm text-[#6E6760] mt-1">
                    {item.specSnapshot?.fabric} {item.specSnapshot?.colour && `· ${item.specSnapshot.colour}`}
                  </p>
                  {item.previewUrl && (
                    <img src={item.previewUrl} alt="AI preview" className="mt-3 rounded-lg border border-[#E5DEC9] max-w-xs" />
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#6E6760]">Qty {item.qty}</p>
                  <p className="font-heading font-semibold">₹{item.amount?.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-right space-y-1 text-sm">
            <p>Subtotal: ₹{data.subtotal?.toFixed(2)}</p>
            <p>Discount: -₹{data.discountAmount?.toFixed(2)}</p>
            <p>GST: +₹{data.gstAmount?.toFixed(2)}</p>
            <p className="text-2xl font-serif-display font-semibold text-[#2A211D] mt-2" data-testid="public-quote-grand-total">
              ₹{data.grandTotal?.toFixed(2)}
            </p>
          </div>

          {data.terms && (
            <div className="mt-6 pt-6 border-t border-[#F3EFE9] text-sm text-[#6E6760]">
              <p className="font-mono-spec text-xs uppercase mb-1">Terms</p>
              <p>{data.terms}</p>
            </div>
          )}

          {data.pdfUrl && (
            <a
              href={data.pdfUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="download-pdf-link"
              className="mt-6 inline-flex bg-[#C85A32] hover:bg-[#B04B26] text-white font-medium px-5 py-2.5 rounded-lg transition-all"
            >
              Download PDF
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
